'use client';

import { type FormEvent, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { BaseModal } from '@/components/ui/BaseModal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { GITHUB_APP_INSTALL_URL } from '@/lib/constants';
import { githubAppInstallEnabled } from '@/lib/flags';
import { theme } from '@/lib/ui/theme';
import {
  submitWaitlist,
  WAITLIST_SEND_FAILED,
} from '@/lib/waitlist/submitWaitlist';

type WaitlistCTAVariant = 'button' | 'inline';

interface WaitlistCTAProps {
  label: string;
  source: string;
  variant?: WaitlistCTAVariant;
}

type Status = 'idle' | 'submitting' | 'success' | 'error';

const TURNSTILE_SCRIPT_SRC =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const WAITLIST_UNAVAILABLE =
  'Early access signup is not configured yet. Please try again later.';
const WAITLIST_BUTTON_LABEL = 'Get early access';
const WAITLIST_SUPPORTING_COPY =
  'Mindrealm is live. Early access includes onboarding with the founder.';

type TurnstileApi = {
  render: (
    container: HTMLElement,
    options: {
      sitekey: string;
      callback: (token: string) => void;
      'expired-callback': () => void;
      'error-callback': () => void;
    }
  ) => string;
  reset: (widgetId: string) => void;
  remove: (widgetId: string) => void;
};

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

function loadTurnstileScript(): Promise<void> {
  if (window.turnstile) {
    return Promise.resolve();
  }

  const existing = document.querySelector<HTMLScriptElement>(
    `script[src="${TURNSTILE_SCRIPT_SRC}"]`
  );
  if (existing) {
    return new Promise((resolve, reject) => {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('turnstile')), {
        once: true,
      });
    });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = TURNSTILE_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.addEventListener('load', () => resolve(), { once: true });
    script.addEventListener('error', () => reject(new Error('turnstile')), {
      once: true,
    });
    document.head.append(script);
  });
}

function TurnstileField({
  siteKey,
  onToken,
  onError,
}: {
  siteKey: string | undefined;
  onToken: (token: string) => void;
  onError: (message: string) => void;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!siteKey || !containerRef.current) {
      return;
    }

    let cancelled = false;

    loadTurnstileScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) {
          return;
        }
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          callback: onToken,
          'expired-callback': () => onToken(''),
          'error-callback': () => onError(WAITLIST_SEND_FAILED),
        });
      })
      .catch(() => onError(WAITLIST_UNAVAILABLE));

    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [siteKey, onToken, onError]);

  if (!siteKey) {
    return (
      <p className="text-destructive text-sm" role="alert">
        {WAITLIST_UNAVAILABLE}
      </p>
    );
  }

  return <div ref={containerRef} className="min-h-16" />;
}

function WaitlistForm({
  source,
  onClose,
}: {
  source: string;
  onClose: () => void;
}) {
  const emailId = useId();
  const websiteId = useId();
  const [email, setEmail] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState<string | null>(null);
  const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY;

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setStatus('submitting');
    setError(null);

    if (!siteKey || !captchaToken) {
      setError(WAITLIST_UNAVAILABLE);
      setStatus('error');
      return;
    }

    const result = await submitWaitlist({
      email,
      source,
      captchaToken,
      honeypot,
    });

    if (result.ok) {
      setStatus('success');
      return;
    }

    setError(result.error);
    setStatus('error');
  }

  if (status === 'success') {
    return (
      <div className="space-y-4 text-center">
        <p
          className="text-foreground/80"
          style={{ fontFamily: theme.fonts.body }}
        >
          Your early access request is in.
          <br />
          Check your email.
        </p>
        <Button type="button" onClick={onClose}>
          Close
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <p className="text-foreground/75 text-sm">{WAITLIST_SUPPORTING_COPY}</p>
      <div className="space-y-2">
        <Label htmlFor={emailId}>Work email</Label>
        <Input
          id={emailId}
          name="email"
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          required
          autoComplete="email"
          disabled={status === 'submitting'}
        />
      </div>

      <TurnstileField
        siteKey={siteKey}
        onToken={setCaptchaToken}
        onError={setError}
      />

      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          left: '-9999px',
          width: '1px',
          height: '1px',
          overflow: 'hidden',
        }}
      >
        <label htmlFor={websiteId}>
          Leave this field empty
          <input
            id={websiteId}
            name="website"
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={honeypot}
            onChange={(event) => setHoneypot(event.target.value)}
          />
        </label>
      </div>

      {error && (
        <p className="text-destructive text-sm" role="alert">
          {error}
        </p>
      )}

      <div className="flex flex-col justify-center gap-3 sm:flex-row sm:justify-end">
        <Button
          type="button"
          variant="outline"
          onClick={onClose}
          disabled={status === 'submitting'}
        >
          Close
        </Button>
        <Button
          type="submit"
          disabled={status === 'submitting' || !siteKey || !captchaToken}
          className="font-semibold text-white transition-all duration-200 hover:!text-white hover:!opacity-100 hover:brightness-110"
          style={{
            background: theme.buttons.gradients.primary.light,
            boxShadow: theme.shadows.button.primary.light.default,
            textShadow: theme.shadows.text.primary,
          }}
        >
          {status === 'submitting'
            ? 'Requesting access...'
            : WAITLIST_BUTTON_LABEL}
        </Button>
      </div>
    </form>
  );
}

function WaitlistModal({
  isOpen,
  source,
  onClose,
}: {
  isOpen: boolean;
  source: string;
  onClose: () => void;
}) {
  if (!isOpen || typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title="Get early access"
      size="sm"
    >
      <WaitlistForm source={source} onClose={onClose} />
    </BaseModal>,
    document.body
  );
}

export function WaitlistCTA({
  label,
  source,
  variant = 'button',
}: WaitlistCTAProps) {
  const [isOpen, setIsOpen] = useState(false);

  if (githubAppInstallEnabled()) {
    if (variant === 'inline') {
      return (
        <a
          href={GITHUB_APP_INSTALL_URL}
          className="underline"
          style={{ textDecorationColor: theme.colors.semantic.info }}
        >
          {label}
        </a>
      );
    }

    return (
      <Button
        size="lg"
        asChild
        className="text-lg font-semibold text-white transition-all duration-200 hover:!text-white hover:!opacity-100 hover:brightness-110"
        style={{
          background: theme.buttons.gradients.primary.light,
          boxShadow: theme.shadows.button.primary.light.default,
          textShadow: theme.shadows.text.primary,
          padding: '1rem 2rem',
          height: 'auto',
        }}
      >
        <a href={GITHUB_APP_INSTALL_URL}>{label}</a>
      </Button>
    );
  }

  if (variant === 'inline') {
    return (
      <>
        <button
          type="button"
          className="underline"
          style={{ textDecorationColor: theme.colors.semantic.info }}
          onClick={() => setIsOpen(true)}
        >
          {WAITLIST_BUTTON_LABEL}
        </button>
        <WaitlistModal
          isOpen={isOpen}
          source={source}
          onClose={() => setIsOpen(false)}
        />
      </>
    );
  }

  return (
    <>
      <Button
        size="lg"
        type="button"
        onClick={() => setIsOpen(true)}
        className="text-lg font-semibold text-white transition-all duration-200 hover:!text-white hover:!opacity-100 hover:brightness-110"
        style={{
          background: theme.buttons.gradients.primary.light,
          boxShadow: theme.shadows.button.primary.light.default,
          textShadow: theme.shadows.text.primary,
          padding: '1rem 2rem',
          height: 'auto',
        }}
      >
        {WAITLIST_BUTTON_LABEL}
      </Button>
      <WaitlistModal
        isOpen={isOpen}
        source={source}
        onClose={() => setIsOpen(false)}
      />
    </>
  );
}
